import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { formatDate } from '../../../utils/Utils';
import TagChips from '../TagChips';
import { getTags, setTags } from '../config/profileApi';
import { fetchTagCatalog } from '../config/tagApi';

// ETİKETLER — kişiye atanmış katalog etiketleri. Atama AT.Write'tır (SPEC F1:
// `PUT {id}/tags { tagIds }` — tam küme gönderilir, sunucu farkı uygular).
// Katalog ATS Ayarları > Aday Etiketleri'nde yönetilir.
export const tagIdsOf = (assigned) => (assigned || []).map((a) => a.tagId || a.id).filter(Boolean);

export default function CandidateTagsTab({
  candidateId, profile, writable, onChanged,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [assigned, setAssigned] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setFetching(true);
    getTags(candidateId)
      .then(({ ok, items }) => {
        if (!alive) return;
        setLoadFailed(!ok);
        setAssigned(items);
      })
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
  }, [candidateId, reloadKey]);

  useEffect(() => {
    if (!writable) return undefined;
    let alive = true;
    fetchTagCatalog().then(({ items }) => { if (alive) setCatalog(items); });
    return () => { alive = false; };
  }, [writable]);

  const catalogById = useMemo(
    () => catalog.reduce((acc, tg) => ({ ...acc, [tg.id]: tg }), {}),
    [catalog],
  );

  // TUZAK: `asyncSelect` YALNIZ `onSearch` okur. Katalog küçük ve zaten bellekte —
  // arama yerel süzülür (tr-TR küçük harf: "İ"/"ı" doğru eşleşsin).
  const schema = useMemo(() => [
    {
      name: 'tagIds',
      label: t('ats.candidateTag.tags'),
      type: 'asyncSelect',
      isMulti: true,
      minChars: 0,
      grid: 12,
      description: t('ats.candidateTag.assignHint'),
      onSearch: async (q) => {
        const needle = (q || '').toLocaleLowerCase('tr-TR');
        return catalog
          .filter((tg) => !needle || (tg.name || '').toLocaleLowerCase('tr-TR').includes(needle))
          .map((tg) => ({ id: tg.id, name: tg.name }));
      },
      onGetById: async (id) => ({ id, name: catalogById[id]?.name || '' }),
    },
  ], [t, catalog, catalogById]);

  // SABİT referans ŞART: GenericFormDrawer `initialValues` değişince formu sıfırlar —
  // her render'da yeni nesne, kullanıcının seçimini anında silerdi.
  const initialValues = useMemo(
    () => ({ tagIds: tagIdsOf(assigned.length ? assigned : profile?.tags) }),
    [assigned, profile],
  );

  const handleSave = useCallback(async (values) => {
    const res = await setTags(candidateId, Array.isArray(values.tagIds) ? values.tagIds : []);
    if (!res.ok) return false;
    setDrawerOpen(false);
    setReloadKey((k) => k + 1);
    if (onChanged) onChanged();
    return true;
  }, [candidateId, onChanged]);

  const columns = useMemo(() => [
    {
      name: 'name',
      label: t('ats.candidateTag.name'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, tableMeta) => <TagChips tags={[assigned[tableMeta.rowIndex]]} />,
      },
    },
    {
      name: 'assignedByName',
      label: t('ats.candidateTag.assignedBy'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'createDate',
      label: t('ats.candidateTag.assignedAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) },
    },
  ], [t, lang, assigned]);

  const toolbarActions = writable ? (
    <Button type="button" data-role="candidate-tags-edit" onClick={() => setDrawerOpen(true)}>
      {t('ats.candidateTag.editAssignments')}
    </Button>
  ) : null;

  return (
    <div data-role="candidate-tags">
      {loadFailed && <LoadErrorBanner message={t('ats.candidateTag.loadFailed')} />}

      {writable && (
        <GenericFormDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={t('ats.candidateTag.editAssignments')}
          schema={schema}
          initialValues={initialValues}
          onSave={handleSave}
        />
      )}

      <DataTable
        title={t('ats.candidateTag.assignedTitle')}
        fetching={fetching}
        columns={columns}
        data={assigned}
        totalRowCount={assigned.length}
        serverSide={false}
        disableFilters
        toolbarActions={toolbarActions}
      />
    </div>
  );
}
